import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosInstance } from 'axios';

import { ManagementService } from '../../../services/ManagementService';
import { ICompany } from '../../../services/Interfaces/IManagemenntService';
import { useApi } from '../../../services/hooks/apiHook';

import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { useAppSelector } from '../../../hooks/hooksRedux';
import { RootState } from '../../../store';

import { DynamicTable, TableColumn } from '../../../components/Table';
import { PageHeadline } from '../../../components/atoms';

const sortCompaniesClientSide = (companies: ICompany[], sortBy: string, sortDirection: string): ICompany[] => {
  const direction = sortDirection === 'asc' ? 1 : -1;

  return [...companies].sort((a, b) => {
    if (sortBy === 'created_at') {
      const aTime = new Date(a.created_at).getTime();
      const bTime = new Date(b.created_at).getTime();
      if (aTime !== bTime) {
        return (aTime - bTime) * direction;
      }
      return (a.id - b.id) * direction;
    }

    if (sortBy === 'id') {
      return (a.id - b.id) * direction;
    }

    const aValue = String(a[sortBy as keyof ICompany] ?? '');
    const bValue = String(b[sortBy as keyof ICompany] ?? '');
    return aValue.localeCompare(bValue) * direction;
  });
};

export const CompanyTransferScreen: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [token] = useLocalStorage('access-token', null);

  const [companies, setCompanies] = useState<ICompany[]>([]);
  const [currentPageResponse, setCurrentPageResponse] = useState<number>(1);
  const [responsePerPage, setResponsePerPage] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [toNextPage, setToNextPage] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [sortByValue, setSortByValue] = useState<string>('created_at');
  const [sortDirectionValue, setSortDirectionValue] = useState<string>('desc');
  const [isLanguageRefreshing, setIsLanguageRefreshing] = useState(false);

  const { legalPersonalitySlug } = useAppSelector((state: RootState) => state.language);
  const api: AxiosInstance = useApi();

  const columns = useMemo(
    () =>
      [
        { columnKey: 'id', header: 'ID' },
        { columnKey: 'ibc_primary', header: t('others.othCompany') },
        { columnKey: 'expire_process', header: t('others.othExpireProcess') },
        { columnKey: 'created_at', header: t('others.othCreationDate') },
        { columnKey: 'jurisdiction', header: t('labels.lblJurisdictions') },
        { columnKey: 'status', header: t('labels.lblStatus') },
        { columnKey: 'actions', header: t('labels.lblActions') }
      ] as TableColumn<any>[],
    [t, i18n.language]
  );

  const typeCompanyRequest = 'transfer_company';

  const headers = useMemo(
    () => ({ 'Content-Language': legalPersonalitySlug, Authorization: `Bearer ${token}` }),
    [legalPersonalitySlug, token]
  );

  const lastFetchKeyRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);
  const initialLanguageSyncRef = useRef(true);

  const buildKey = useCallback(
    (page: number, sortBy: string, sortDirection: string) =>
      `${token}|${legalPersonalitySlug}|${page}|${sortBy}|${sortDirection}`,
    [legalPersonalitySlug, token]
  );

  const fetchListCompanies = useCallback(
    async (page: number, sortBy: string, sortDirection: string, force = false, showSpinner = true) => {
      if (!token) {
        lastFetchKeyRef.current = null;
        setCompanies([]);
        setResponsePerPage(0);
        setCurrentPageResponse(1);
        setTotalItems(0);
        setToNextPage(0);
        return;
      }

      const key = buildKey(page, sortBy, sortDirection);

      if (!force && lastFetchKeyRef.current === key) {
        setSortByValue(sortBy);
        setSortDirectionValue(sortDirection);
        return;
      }

      if (isFetchingRef.current) {
        return;
      }

      isFetchingRef.current = true;
      if (showSpinner) {
        setIsLoading(true);
      }
      setSortByValue(sortBy);
      setSortDirectionValue(sortDirection);

      try {
        const { data } = await ManagementService.listCompanies(api, typeCompanyRequest, sortBy, sortDirection, page, {
          headers
        });

        lastFetchKeyRef.current = key;
        // Fallback when API returns unsorted/asc pages (e.g. staging without created_at support)
        setCompanies(sortCompaniesClientSide(data.data ?? [], sortBy, sortDirection));
        setResponsePerPage(data.per_page);
        setCurrentPageResponse(data.current_page);
        setTotalItems(data.total);
        setToNextPage(data.last_page);
      } catch (error) {
        console.error('Error fetch service list companies', error);
        lastFetchKeyRef.current = null;
        setCompanies([]);
        setResponsePerPage(0);
        setCurrentPageResponse(1);
        setTotalItems(0);
        setToNextPage(0);
      } finally {
        isFetchingRef.current = false;
        setIsLoading(false);
      }
    },
    [api, buildKey, headers, token]
  );

  useEffect(() => {
    if (!token) {
      setCompanies([]);
      setResponsePerPage(0);
      setCurrentPageResponse(1);
      setTotalItems(0);
      setToNextPage(0);
      lastFetchKeyRef.current = null;
      return;
    }

    const key = buildKey(1, sortByValue, sortDirectionValue);
    if (lastFetchKeyRef.current === key) {
      return;
    }

    void fetchListCompanies(1, sortByValue, sortDirectionValue, true, false);
  }, [buildKey, fetchListCompanies, sortByValue, sortDirectionValue, token]);

  useEffect(() => {
    if (!token) {
      return;
    }

    if (initialLanguageSyncRef.current) {
      initialLanguageSyncRef.current = false;
      return;
    }

    setIsLanguageRefreshing(true);
    const timeout = setTimeout(() => {
      void fetchListCompanies(1, sortByValue, sortDirectionValue, true, false).finally(() => {
        setIsLanguageRefreshing(false);
      });
    }, 700);

    return () => {
      clearTimeout(timeout);
      setIsLanguageRefreshing(false);
    };
  }, [fetchListCompanies, sortByValue, sortDirectionValue, token, legalPersonalitySlug]);

  return (
    <div className="min-h-[300px]">
      <div className="mb-6">
        <PageHeadline title={t('others.otheTransferredCompanies')} />
      </div>

      <div className="relative min-h-[280px]">
        {(isLoading || isLanguageRefreshing) && (
          <div className="absolute inset-0 z-40 flex flex-col items-center justify-center bg-white">
            <div className="flex flex-col items-center gap-3">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#E1E9F5] border-t-[#0C1FAA]"></div>
              <span className="text-xs font-medium text-[#6A7A90]">
                {t('labels.lblLoading') || 'Loading...'}
              </span>
            </div>
          </div>
        )}

        <DynamicTable
          data={companies}
          columns={columns}
          itemsPerPage={responsePerPage}
          currentPage={currentPageResponse}
          totalItems={totalItems}
          toNextPage={toNextPage}
          onPageChange={(page, sortBy, sortDirection) => {
            const normalizedSortBy = sortBy ?? 'created_at';
            const normalizedSortDirection = sortDirection ?? 'desc';
            const key = buildKey(page, normalizedSortBy, normalizedSortDirection);
            void fetchListCompanies(
              page,
              normalizedSortBy,
              normalizedSortDirection,
              lastFetchKeyRef.current !== key
            );
          }}
          sortBy={sortByValue}
          sortDirection={sortDirectionValue}
        />
      </div>
    </div>
  );
};
